import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Download, Search, X } from "lucide-react";
import { api, errText } from "../../lib/api";

export const money = (n) => (n === null || n === undefined ? "—" : Math.round(n).toLocaleString("fr-FR"));

// Live MAD → EUR, refreshed server-side every 12 hours. One fetch per admin session.
let _fxCache = null;
let _fxPromise = null;
export const useEurRate = () => {
  const [rate, setRate] = useState(_fxCache);
  useEffect(() => {
    if (_fxCache) return;
    _fxPromise = _fxPromise || api.get("/fx/rates").then((r) => r.data).catch(() => null);
    _fxPromise.then((d) => {
      if (!d?.mid?.EUR) return;
      _fxCache = { eur: d.mid.EUR, updated_at: d.updated_at };
      setRate(_fxCache);
    });
  }, []);
  return rate;
};

/** "≈ €28.40" for a dirham amount, or "" until the rate has landed. */
export const eurOf = (rate, mad) => {
  const value = Number(mad);
  if (!rate?.eur || !Number.isFinite(value) || value <= 0) return "";
  return `≈ €${(value * rate.eur).toFixed(2)}`;
};

export const shortDate = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");

export const stamp = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

const TONES = {
  good: "bg-success/10 text-success border-success/30",
  warn: "bg-warning/10 text-warning border-warning/40",
  bad: "bg-danger/10 text-danger border-danger/30",
  plain: "bg-plaster-deep text-muted border-brass-light",
  ink: "bg-ink/5 text-ink border-ink/20",
  brass: "bg-brass/15 text-brass border-brass/50",
};

export const Badge = ({ children, tone = "plain", testid, title, className = "" }) => (
  <span data-testid={testid} title={title} className={`inline-block border px-2 py-0.5 text-[11px] tracking-wide ${TONES[tone] || TONES.plain} ${className}`}>
    {children}
  </span>
);

export const statusTone = (status) => {
  if (["delivered", "completed", "active", "published", "paid"].includes(status)) return "good";
  if (["issue_reported", "cancelled", "refunded_full", "refunded_partial", "suspended", "payment_failed", "rejected"].includes(status)) return "bad";
  if (["routing", "awaiting_acceptance", "pending_review", "pending_payment", "paused"].includes(status)) return "warn";
  return "ink";
};

export const RiskDot = ({ level }) => (
  <span className={`inline-block w-2 h-2 rounded-full ${level === "red" ? "bg-danger" : level === "amber" ? "bg-warning" : "bg-success"}`} />
);

export const Stat = ({ label, value, hint, to, testid, tone }) => {
  const inner = (
    <>
      <p className="smallcaps">{label}</p>
      <p className={`font-display text-3xl tabular ${tone === "bad" ? "text-danger" : tone === "warn" ? "text-warning" : "text-ink"}`}>{value}</p>
      {hint ? <p className="text-xs text-muted mt-1">{hint}</p> : null}
    </>
  );
  const base = "card p-4 block transition-transform duration-150";
  return to ? (
    <Link to={to} data-testid={testid} className={`${base} hover:-translate-y-0.5 hover:border-brass`}>{inner}</Link>
  ) : (
    <div data-testid={testid} className={base}>{inner}</div>
  );
};

export const Panel = ({ title, action, children, testid, className = "" }) => (
  <section className={`card p-5 space-y-3 ${className}`} data-testid={testid}>
    <div className="flex items-center gap-3">
      <h3 className="smallcaps">{title}</h3>
      <span className="ms-auto">{action}</span>
    </div>
    {children}
  </section>
);

export const KeyRow = ({ label, children, testid }) => (
  <div className="flex gap-3 text-sm border-b border-brass-light py-1.5 last:border-0" data-testid={testid}>
    <span className="text-muted min-w-36">{label}</span>
    <span className="text-ink text-end ms-auto break-all">{children ?? "—"}</span>
  </div>
);

export const Select = ({ value, onChange, options, testid, placeholder }) => (
  <select className="ctl min-w-32" data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}>
    <option value="">{placeholder}</option>
    {options.map((o) => (
      <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
    ))}
  </select>
);

export const useFilterOptions = () => {
  const [options, setOptions] = useState({ cities: [], florists: [], order_statuses: [], countries: [], payment_states: [], gateways: ["stripe", "tryb"] });
  useEffect(() => {
    api.get("/admin/filters").then((r) => setOptions(r.data)).catch(() => {});
  }, []);
  return options;
};

export function useTable(path, defaults = {}) {
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const fromUrl = useMemo(() => Object.fromEntries(sp.entries()), [sp]);
  const params = useMemo(() => {
    const merged = { page: 1, per_page: 25, ...defaults, ...fromUrl };
    delete merged.order;
    delete merged.payment;
    delete merged.tab;
    return { ...merged, page: Number(merged.page) || 1, per_page: Number(merged.per_page) || 25 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromUrl]);

  const set = useCallback((patch) => {
    const next = { ...fromUrl, ...patch };
    if (!("page" in patch)) next.page = 1;
    Object.keys(next).forEach((k) => {
      if (next[k] === "" || next[k] === null || next[k] === undefined) delete next[k];
    });
    setSp(next, { replace: true });
  }, [fromUrl, setSp]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data: d } = await api.get(path, { params });
      setData(d);
    } catch (e) {
      setError(errText(e));
    }
    setLoading(false);
  }, [path, params]);

  useEffect(() => { load(); }, [load]);

  const download = useCallback(async (name) => {
    const r = await api.get(path, { params: { ...params, format: "csv" }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [path, params]);

  return { data, loading, error, params, set, reload: load, download, rows: data?.rows || [] };
}

const Pager = ({ table, testid }) => {
  const { data, params, set } = table;
  const pages = data?.pages || 1;
  const page = params.page;
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm" data-testid={`${testid}-pager`}>
      <button className="btn-ghost text-xs flex items-center gap-1 disabled:opacity-30" data-testid={`${testid}-prev`}
              disabled={page <= 1} onClick={() => set({ page: page - 1 })}>
        <ChevronLeft size={14} /> Previous
      </button>
      <span className="text-muted tabular" data-testid={`${testid}-page-label`}>Page {page} of {pages}</span>
      <button className="btn-ghost text-xs flex items-center gap-1 disabled:opacity-30" data-testid={`${testid}-next`}
              disabled={page >= pages} onClick={() => set({ page: page + 1 })}>
        Next <ChevronRight size={14} />
      </button>
      <form className="flex items-center gap-2 ms-2" onSubmit={(e) => { e.preventDefault(); const v = Number(new FormData(e.target).get("jump")); if (v >= 1 && v <= pages) set({ page: v }); }}>
        <input name="jump" className="ctl w-16 text-center" placeholder={String(page)} data-testid={`${testid}-jump`} />
        <button className="btn-ghost text-xs">Go</button>
      </form>
    </div>
  );
};

export const DataTable = ({ table, columns, name, testid, rowId = (r) => r.id, filters, empty = "Nothing here yet.",
                           searchPlaceholder = "Search…", right, selection, bulkBar, mobileRow }) => {
  const { data, loading, error, params, set, rows, download } = table;
  const [q, setQ] = useState(params.q || "");
  useEffect(() => { setQ(params.q || ""); }, [params.q]);
  const sortBy = (key) => set({ sort: key, dir: params.sort === key && params.dir !== "asc" ? "asc" : "desc" });

  const allChecked = selection && rows.length > 0 && rows.every((r) => selection.selected.has(rowId(r)));
  const toggleAll = () => {
    if (!selection) return;
    if (allChecked) rows.forEach((r) => selection.remove(rowId(r)));
    else rows.forEach((r) => selection.add(rowId(r), r));
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2">
        <form className="relative w-full sm:w-auto" onSubmit={(e) => { e.preventDefault(); set({ q }); }}>
          <Search size={14} className="absolute start-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <input className="ctl ps-8 w-full sm:w-60" placeholder={searchPlaceholder} value={q}
                 data-testid={`${testid}-search`} onChange={(e) => setQ(e.target.value)} />
          {q ? (
            <button type="button" className="absolute end-2 top-1/2 -translate-y-1/2 text-muted" data-testid={`${testid}-search-clear`}
                    onClick={() => { setQ(""); set({ q: "" }); }}><X size={13} /></button>
          ) : null}
        </form>
        {filters}
        <div className="sm:ms-auto flex flex-wrap items-center gap-2">
          {right}
          <span className="text-xs text-muted tabular" data-testid={`${testid}-total`}>{data?.total ?? 0} rows</span>
          <select className="ctl" value={params.per_page} data-testid={`${testid}-per-page`}
                  onChange={(e) => set({ per_page: e.target.value })}>
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{`${n} / page`}</option>)}
          </select>
          <button className="btn-ghost text-[11px] flex items-center gap-1" data-testid={`${testid}-csv`}
                  onClick={() => download(name)}><Download size={13} /> CSV</button>
        </div>
      </div>

      {selection && selection.selected.size > 0 ? (
        <div className="flex flex-wrap items-center gap-2 bg-ink text-plaster px-3 py-2 border border-brass" data-testid={`${testid}-bulk-bar`}>
          <span className="text-xs tabular">{selection.selected.size} selected</span>
          {bulkBar}
          <button className="ms-auto text-xs underline underline-offset-2" data-testid={`${testid}-bulk-clear`}
                  onClick={() => selection.clear()}>Clear</button>
        </div>
      ) : null}

      {error ? <p className="text-danger text-sm" data-testid={`${testid}-error`}>{error}</p> : null}

      {mobileRow ? (
        <div className="lg:hidden border border-brass-light bg-white divide-y divide-brass-light" data-testid={`${testid}-cards`}>
          {rows.map((r) => (
            <div key={rowId(r)} className="p-3" data-testid={`${testid}-card-${rowId(r)}`}>{mobileRow(r)}</div>
          ))}
          {loading ? <p className="p-4 text-sm text-muted">Loading…</p> : null}
          {!loading && rows.length === 0 ? <p className="p-4 text-sm text-muted">{empty}</p> : null}
        </div>
      ) : null}

      <div className={`overflow-x-auto border border-brass-light bg-white ${mobileRow ? "hidden lg:block" : ""}`}>
        <table className="w-full text-sm">
          <thead className="bg-plaster-deep">
            <tr>
              {selection ? (
                <th className="w-8 px-2">
                  <input type="checkbox" checked={allChecked} onChange={toggleAll}
                         data-testid={`${testid}-select-all`} />
                </th>
              ) : null}
              {columns.map((c) => (
                <th key={c.key} className={`text-start px-3 py-2 smallcaps whitespace-nowrap ${c.className || ""}`}>
                  {c.sortable ? (
                    <button className="flex items-center gap-1" data-testid={`${testid}-sort-${c.key}`} onClick={() => sortBy(c.key)}>
                      {c.label}
                      {params.sort === c.key ? (params.dir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : null}
                    </button>
                  ) : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const id = rowId(r);
              const checked = selection?.selected.has(id);
              return (
                <tr key={id} className={`border-t border-brass-light hover:bg-plaster/60 ${checked ? "bg-brass/5" : ""}`} data-testid={`${testid}-row-${id}`}>
                  {selection ? (
                    <td className="px-2 align-top">
                      <input type="checkbox" checked={!!checked} data-testid={`${testid}-select-${id}`}
                             onChange={(e) => e.target.checked ? selection.add(id, r) : selection.remove(id)} />
                    </td>
                  ) : null}
                  {columns.map((c) => (
                    <td key={c.key} className={`px-3 py-2 align-top ${c.className || ""}`}>
                      {c.render ? c.render(r) : (r[c.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
        {loading ? <p className="p-4 text-sm text-muted" data-testid={`${testid}-loading`}>Loading…</p> : null}
        {!loading && rows.length === 0 ? <p className="p-4 text-sm text-muted" data-testid={`${testid}-empty`}>{empty}</p> : null}
      </div>

      <Pager table={table} testid={testid} />
    </div>
  );
};

export function useSelection() {
  const [selected, setSelected] = useState(() => new Map());
  const add = useCallback((id, row) => setSelected((prev) => {
    const next = new Map(prev); next.set(id, row); return next;
  }), []);
  const remove = useCallback((id) => setSelected((prev) => {
    const next = new Map(prev); next.delete(id); return next;
  }), []);
  const clear = useCallback(() => setSelected(new Map()), []);
  return { selected, add, remove, clear, ids: Array.from(selected.keys()), rows: Array.from(selected.values()) };
}

export const Drawer = ({ title, subtitle, onClose, children, testid, actions }) => (
  <div className="fixed inset-0 scrim z-40 flex justify-end" data-testid={testid} onClick={onClose}>
    <div className="bg-plaster w-full max-w-3xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="sticky top-0 bg-ink text-plaster px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 z-10">
        <div className="min-w-0">
          <h2 className="font-display text-xl sm:text-2xl truncate">{title}</h2>
          {subtitle ? <p className="text-xs text-plaster/70 truncate">{subtitle}</p> : null}
        </div>
        <div className="ms-auto flex items-center gap-2 sm:gap-3 shrink-0">
          {actions}
          <button onClick={onClose} data-testid={`${testid}-close`}><X size={18} /></button>
        </div>
      </div>
      <div className="p-4 sm:p-6 space-y-5">{children}</div>
    </div>
  </div>
);

export const TabBar = ({ tabs, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1 border-b border-brass-light" data-testid={testid}>
    {tabs.map(([key, label, count]) => (
      <button key={key} data-testid={`${testid}-${key}`} onClick={() => onChange(key)}
              className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${value === key ? "border-brass text-ink" : "border-transparent text-muted hover:text-ink"}`}>
        {label}{count !== undefined && count !== null ? <span className="tabular text-xs text-muted"> {count}</span> : null}
      </button>
    ))}
  </div>
);

export const OrderLink = ({ number, id, testid }) => (
  <Link to={`/admin/orders?order=${id}`} className="underline decoration-brass underline-offset-2" data-testid={testid}>{number}</Link>
);

export const ShopLink = ({ florist }) => (florist ? (
  <Link to={`/admin/florists/${florist.id}`} className="underline decoration-brass underline-offset-2">{florist.shop_name}</Link>
) : <span className="text-warning">unassigned</span>);

export const CustomerLink = ({ email }) => (email ? (
  <Link to={`/admin/customers/${encodeURIComponent(email)}`} className="underline decoration-brass underline-offset-2">{email}</Link>
) : "—");
